import 'reflect-metadata';
import { fetchWithAuth } from '../apis/fetchWithAuth';
import {
  ApiResponse,
  ApiResponseWithPagination
} from '../type/response/apiResponse';

const BASE_URL_KEY = Symbol('baseUrl');

function toURLSearchParams(obj: Record<string, unknown>): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(obj)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .forEach(([k, v]) => {
      if (Array.isArray(v)) {
        v.forEach(item => {
          if (item !== null && item !== undefined && item !== '') {
            params.append(k, String(item));
          }
        });
        return;
      }
      params.append(k, String(v));
    });
  return params;
}

export function RequestMapping(basePath: string) {
  return function <T extends { new (...args: any[]): {} }>(constructor: T) {
    Reflect.defineMetadata(BASE_URL_KEY, basePath, constructor);

    const NewClass = class extends constructor {};

    Object.getOwnPropertyNames(constructor).forEach(key => {
      if (key !== 'prototype' && key !== 'length' && key !== 'name') {
        Object.defineProperty(
          NewClass,
          key,
          Object.getOwnPropertyDescriptor(constructor, key)!
        );
      }
    });

    return NewClass;
  };
}

function createMethodDecorator(method: string) {
  return function (path?: string) {
    return function (
      _target: any,
      _propertyKey: string,
      descriptor: PropertyDescriptor
    ) {
      descriptor.value = async function (...args: any[]) {
        const _path = path ?? '';
        const constructor = this.constructor;
        const baseUrl = Reflect.getMetadata(BASE_URL_KEY, constructor) || '';
        let fullUrl = `${baseUrl}${_path}`;

        const requestConfig: RequestInit = {
          method: method.toUpperCase(),
          headers: {
            'Content-Type': 'application/json'
          }
        };

        const pathParamMatches = _path.match(/\{([^}]+)}/g) || [];
        const pathParamCount = pathParamMatches.length;

        if (pathParamCount > 0) {
          pathParamMatches.forEach((placeholder, index) => {
            if (args[index] !== undefined && args[index] !== null) {
              fullUrl = fullUrl.replace(placeholder, args[index]);
            } else {
              throw new Error(
                `Path parameter ${placeholder} is missing a corresponding argument value`
              );
            }
          });
        }

        const lastArg = args[args.length - 1];
        const isLastArgObject = typeof lastArg === 'object' && lastArg !== null;

        if (
          (method.toUpperCase() === 'GET' ||
            method.toUpperCase() === 'DELETE') &&
          isLastArgObject
        ) {
          const params = toURLSearchParams(lastArg);
          fullUrl = `${fullUrl}?${params.toString()}`;
        }

        if (['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
          const bodyArgIndex = pathParamCount > 0 ? pathParamCount : 0;
          if (args[bodyArgIndex] !== undefined && args[bodyArgIndex] !== null) {
            if (args[bodyArgIndex] instanceof FormData) {
              requestConfig.body = args[bodyArgIndex];
              delete (requestConfig.headers as Record<string, string>)[
                'Content-Type'
              ];
            } else if (args[bodyArgIndex] instanceof Blob) {
              requestConfig.body = args[bodyArgIndex];
              (requestConfig.headers as Record<string, string>)[
                'Content-Type'
              ] = 'application/octet-stream';
            } else {
              requestConfig.body = JSON.stringify(args[bodyArgIndex]);
            }
          }

          const queryArg = args[bodyArgIndex + 1];
          const isQueryArgObject =
            typeof queryArg === 'object' && queryArg !== null;
          if (isQueryArgObject) {
            const params = toURLSearchParams(queryArg);
            fullUrl = `${fullUrl}?${params.toString()}`;
          }
        }

        const response = await fetchWithAuth<
          ApiResponse | ApiResponseWithPagination
        >(fullUrl, requestConfig);
        return response?.data ?? null;
      };
      return descriptor;
    };
  };
}

export const GetMapping = createMethodDecorator('GET');
export const PostMapping = createMethodDecorator('POST');
export const PutMapping = createMethodDecorator('PUT');
export const DeleteMapping = createMethodDecorator('DELETE');

export default abstract class HttpExecutor {
  public defaultValue<T>(): Promise<T> {
    return Promise.resolve(null as unknown as T);
  }
}
